import { useNavigate } from "react-router-dom";
import { Activity, Play, X } from "react-feather";
import {
  selectRecipesWithInProgressCook,
  abandonCookingProgress,
  type RecipeCookingProgress,
  useAppDispatch,
  useAppSelector,
  useT,
} from "@reciperoll/core";
import { RecipeImage } from "../RecipeImage";
import { useToast } from "../Toast";

// In-progress cooks: the most recent one is featured with a progress bar, the
// rest sit in a compact row beneath. Resume opens cook mode; the little "×"
// discards a session (same wording as the cook-view stop button).
//
// This block needs explicit `dark:` overrides (unlike the token-driven surfaces
// elsewhere): in light it's a soft terracotta panel with white cards; in dark a
// flat terracotta tint goes muddy, so we build real elevation instead —
// page (paper) < panel (surface) < cards (surface-2).
export default function ContinueCooking() {
  const t = useT();
  const items = useAppSelector(selectRecipesWithInProgressCook);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  if (!items.length) return null;

  const resume = (recipeId: string) => navigate(`/recipe/${recipeId}/cook`);
  const confirmStop = (item: RecipeCookingProgress) => {
    if (confirm(t("cook.stopConfirmWeb", { title: item.recipe.title }))) {
      dispatch(abandonCookingProgress(item.recipe.id));
      toast.show(t("cook.stoppedCooking", { title: item.recipe.title }));
    }
  };
  const pct = (i: RecipeCookingProgress) =>
    i.totalSteps ? (i.currentStep / i.totalSteps) * 100 : 0;

  const [featured, ...rest] = items;

  // Shared card styling for the featured + secondary cooks.
  const cardClass =
    "bg-surface dark:bg-surface-2 border border-line hover:bg-surface-2 dark:hover:bg-line transition-colors cursor-pointer";

  return (
    <div className="bg-terracotta/10 dark:bg-surface border border-terracotta/10 dark:border-line rounded-3xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-[18px] h-[18px] text-terracotta" />
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-terracotta-ink">
          {t("recipe.continueCooking")}
        </p>
        {items.length > 1 && (
          <span className="ml-auto bg-surface dark:bg-surface-2 border border-line rounded-full px-2.5 py-0.5 text-xs font-semibold text-terracotta-ink">
            {t("cook.onTheGo", { count: items.length })}
          </span>
        )}
      </div>

      {/* Featured — the most recent cook */}
      <div className="relative">
        <button
          onClick={() => resume(featured.recipe.id)}
          className={`w-full flex items-center gap-3 rounded-2xl p-3 text-left ${cardClass}`}
        >
          <div className="w-16 h-16 rounded-xl overflow-hidden bg-terracotta-wash shrink-0">
            <RecipeImage recipe={featured.recipe} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg font-semibold text-ink mb-1 truncate">
              {featured.recipe.title}
            </p>
            {!!featured.totalSteps && (
              <>
                <p className="text-sm text-ink-soft mb-1.5">
                  {t("cook.stepOf", { current: featured.currentStep, total: featured.totalSteps })}
                </p>
                <div className="h-1.5 bg-ink/10 rounded-full">
                  <div
                    className="h-full bg-terracotta rounded-full transition-all"
                    style={{ width: `${pct(featured)}%` }}
                  />
                </div>
              </>
            )}
          </div>
          <div className="w-11 h-11 rounded-full bg-terracotta flex items-center justify-center shrink-0">
            <Play className="w-[18px] h-[18px] text-white ml-0.5" fill="currentColor" stroke="none" />
          </div>
        </button>
        <button
          onClick={() => confirmStop(featured)}
          aria-label={t("recipe.stopCooking")}
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-surface dark:bg-surface-2 border border-line flex items-center justify-center text-ink-soft hover:text-ink cursor-pointer"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Any other in-progress cooks */}
      {rest.length > 0 && (
        <div className="flex gap-2 overflow-x-auto mt-3 pb-1 no-scrollbar">
          {rest.map((item) => (
            <button
              key={item.recipe.id}
              onClick={() => resume(item.recipe.id)}
              className={`flex items-center gap-2 rounded-xl p-2 pr-3 w-44 shrink-0 text-left ${cardClass}`}
            >
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-terracotta-wash shrink-0">
                <RecipeImage recipe={item.recipe} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-ink truncate">
                  {item.recipe.title}
                </p>
                {!!item.totalSteps && (
                  <p className="text-xs text-ink-soft">
                    {t("cook.stepShort", { current: item.currentStep, total: item.totalSteps })}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
